import type CMSConnector from "./connector";
import type { TokenGateCredentials } from "./types";
import { removeQueryParams } from "./util";

export interface TokenGateState {
  has_access: boolean;
  access_key: string | null;
}

export default class TokenGate {
  cms: CMSConnector;
  access_key: string | null = null;
  has_access = false;

  constructor(cms: CMSConnector) {
    this.cms = cms;
  }

  get productId(): string | null {
    return this.cms.campaign.token_gate.product_id;
  }

  get gatedCampaignUrl(): string | null {
    return this.cms.campaign.token_gated_campaign_url;
  }

  get tokenGateUrl(): string | null {
    return this.cms.campaign.token_gate_url;
  }

  private get localStorageKey(): string {
    const c = this.cms.campaign;
    return `${c.app}:${c.parent_id || c.id}:token_gate_access:${
      this.productId
    }`;
  }

  private dispatchStateChange(): void {
    document.dispatchEvent(
      new CustomEvent("umg:tokenGate:stateChanged", {
        detail: {
          has_access: this.has_access,
          access_key: this.access_key,
        },
      })
    );
  }

  // Are we in the pre-page? Pre-page campaign should have an attribute to say so
  // E.g. "is_token_gate: true"
  // If so and token is valid -> redirect to parent
  // If in the main campaign and token is invalid -> redirect to the child with is_token_gate
  // Add token_gate_url property which is the URL of child campaign
  async init() {
    // This should be called on page load to confirm whether user already has
    // Do not redirect automatically, app can listen for state change and redirect if needed
    await this.initValidateAcess();
    this.dispatchStateChange();
  }

  async initValidateAcess(): Promise<void> {
    if (!this.cms.campaign.token_gate.enabled || !this.productId) {
      this.has_access = true;
      return;
    }
    // Check if access_key is in url query. May be after redirecting from pre-page to campaign
    const params = new URLSearchParams(window.location.search);
    let access_key = params.get("token_gate_access_key");
    if (access_key) {
      this.has_access = await this.cms.api.tokenGate.verifyAccessKey(
        this.cms.campaign.parent_id || this.cms.campaign.id,
        access_key
      );
      // always clear the param from URL
      window.history.replaceState(
        {},
        document.title,
        removeQueryParams(window.location.href, ["token_gate_access_key"])
      );
      if (this.has_access) {
        // URL token is valid, save it and go on
        window.localStorage.setItem(this.localStorageKey, access_key);
        return;
      } else {
        // delete the token from local storage as we have an invalid one in url
        window.localStorage.removeItem(this.localStorageKey);
      }
    }
    // Get the local storage access key
    access_key = window.localStorage.getItem(this.localStorageKey);
    if (!access_key) {
      this.has_access = false;
      return;
    }

    // Verify the storage access key
    this.has_access = await this.cms.api.tokenGate.verifyAccessKey(
      this.cms.campaign.parent_id || this.cms.campaign.id,
      access_key
    );
    if (!this.has_access) {
      window.localStorage.removeItem(this.localStorageKey);
    } else {
      this.access_key = access_key;
    }
  }

  /**
   * Get the URL to the gated campaign with the access key in param
   * @returns URL to the gated campaign or null if current campaign is not a token gate
   */
  getGatedCampaignUrl(): string | null {
    if (
      !this.cms.campaign.token_gated_campaign_url ||
      !this.cms.campaign.is_token_gate
    ) {
      return null;
    }
    const url = new URL(this.cms.campaign.token_gated_campaign_url);
    url.searchParams.set("token_gate_access_key", this.access_key || "");
    return url.toString();
  }

  redirectIfHasAccess(): boolean {
    // If currently on the pre-page, redirect to campaign if token is valid
    const url = this.getGatedCampaignUrl();
    if (url && this.has_access) {
      window.location.href = url.toString();
      return true;
    }
    return false;
  }

  redirectIfBlocked(): boolean {
    // If we're currently on the campaign page, redirect to the pre-page if we don't have access
    if (!this.cms.campaign.is_token_gate && this.cms.campaign.token_gate_url) {
      if (!this.has_access) {
        window.location.href = this.cms.campaign.token_gate_url;
        return true;
      }
    }
    return false;
  }

  async verifyByProvider(credentials: TokenGateCredentials): Promise<boolean> {
    // Check the token gate provider (12x12) through cms backend
    // Verify token is owned by user and receive access_key confirming it
    // access_key is used subsequently to verify without re-requiring provider login
    const result = await this.cms.api.tokenGate.providerCheck(
      // Use the ID of the gated campaign for consistency when checking
      // in pre-page and campaign side
      this.cms.campaign.parent_id || this.cms.campaign.id,
      credentials
    );

    if (result.has_access && result.access_key) {
      window.localStorage.setItem(this.localStorageKey, result.access_key);
    }
    this.access_key = result.access_key;
    this.has_access = result.has_access;

    this.dispatchStateChange();

    return this.has_access;
  }
}
